"use client";
import { ButtonComponent, ModalComponent } from "@/components";
import { IMAGES, WEB_COLORS } from "@/lib/constants";
import { useGetApplicationConfigQuery } from "@/lib/redux";
import { Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";

// ApplicationConfigWrapper: provides a context and modal for server maintenance, opens on 503 error
import { createContext, useContext } from "react";

const ServerMaintenanceContext = createContext({
  trigger: () => {},
  setConfig: () => {},
});

export const useServerMaintenance = () => useContext(ServerMaintenanceContext);

const DEFAULT_CONFIG = {
  isServerDown: true,
  title: "Server Maintenance",
  description:
    "Our servers are currently under maintenance. Please try again later.",
  endDateTime: null,
};

const ApplicationConfigWrapper = ({ children }) => {
  const { data: applicationConfigData } = useGetApplicationConfigQuery();
  const serverConfiguration = applicationConfigData?.data?.serverconfig;
  const t = useTranslations("ApplicationConfig");
  const [open, setOpen] = useState(false);
  const [serverConfig, setServerConfig] = useState(DEFAULT_CONFIG);
  const [dateStatus, setDateStatus] = useState(true);
  const [value, setValue] = useState(0);
  const intervalID = useRef(null);

  // Expose trigger function globally for baseQuery to call
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.__TRIGGER_SERVER_MAINTENANCE__ = (config) => {
        setServerConfig({ ...DEFAULT_CONFIG, ...config });
        setOpen(true);
      };
      return () => {
        window.__TRIGGER_SERVER_MAINTENANCE__ = undefined;
      };
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    intervalID.current = setInterval(() => {
      if (serverConfig?.isServerDown || serverConfiguration?.isServerDown) {
        var date = dayjs(
          serverConfig?.endDateTime || serverConfiguration?.endDateTime,
          "DD/MM/YYYY hh:mmA"
        );
        var now = dayjs();
        if (now > date) {
          setDateStatus(false);
        } else {
          setDateStatus(true);
        }
        setValue((v) => v + 1);
      } else {
        clearInterval(intervalID.current);
        intervalID.current = null;
        setDateStatus(false);
      }
    }, 120000);
    return () => clearInterval(intervalID.current);
  }, [open, value, serverConfig, serverConfiguration]);

  const handleRoute = (isDown) => {
    if (!isDown) {
      setOpen(false);
      window.location.href = "/";
      window.location.reload();
    }
  };

  useEffect(() => {
    if (serverConfiguration?.isServerDown) {
      setOpen(serverConfiguration?.isServerDown);
      setOpen(true);
    }
  }, [serverConfiguration?.isServerDown]);

  return (
    <ServerMaintenanceContext.Provider
      value={{
        trigger:
          typeof window !== "undefined"
            ? window.__TRIGGER_SERVER_MAINTENANCE__
            : () => {},
        setConfig: setServerConfig,
      }}
    >
      {children}
      <ModalComponent
        open={open}
        fullScreen={true}
        fullWidth
        maxWidth="lg"
        maxhight="auto"
        closeButtonRequired={false}
      >
        <Stack
          alignItems={"center"}
          rowGap={2}
          justifyContent={"center"}
          spacing={2}
          width={"100%"}
          height={"100%"}
        >
          <Image
            src={IMAGES.webServerMaintenance}
            alt="server maintenance"
            height={300}
            width={300}
          />
          <Typography variant="h5" align="center" color={WEB_COLORS.errorColor}>
            {serverConfiguration?.title || serverConfig?.title}
          </Typography>
          <Typography variant="body1" align="center">
            {serverConfiguration?.description || serverConfig?.description}
          </Typography>
          <ButtonComponent
            type="submit"
            buttonStyle={{
              width: "100%",
              backgroundColor: dateStatus
                ? WEB_COLORS.errorColor
                : WEB_COLORS.primary,
            }}
            onClick={() => handleRoute(dateStatus)}
          >
            {dateStatus ? t("maintenanceError") : t("maintenanceRefresh")}
          </ButtonComponent>
        </Stack>
      </ModalComponent>
    </ServerMaintenanceContext.Provider>
  );
};

export default ApplicationConfigWrapper;
